/**
 * @file StatementRunPeople.js
 * @version 1.3.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-12
 * @modified 2026-10-20
 *
 * The strip at the top of Generate Statements for a run that has been handed
 * over or shared: who is on it, who handed it to you and when (with their
 * message), the last change a colleague made, and what has already gone out on
 * the run. Shows nothing for a run only you have ever worked on. Offers "Take
 * over this run" to an admin who is not on it, or to anyone when nobody live
 * is left on it. A run handed to you for Accounts' check says so (1.2.0).
 *
 * 1.3.0 (2026-10-20, the statements look): `variant="avatars"` — the people
 * as initials, for the screen's header; `showPeople={false}` leaves the names
 * off the strip (the header shows them) so the strip is one line about the
 * hand-over and changes only.
 */

import React from 'react';
import { useSlideOutButton } from './SlideOutButton';
import { peopleText, lastChangeText, deliveriesText, whenText } from '../utils/statementRunWork';
import './StatementHandOver.css';

const initials = (name) => String(name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('') || '?';

/** The people on the run as initials (the header). Nothing for a run only you are on. */
function PeopleAvatars({ work, meId }) {
  if (!work?.ready || work.missing) return null;
  const people = work.people || [];
  if (people.length < 2 && !(people.length === 1 && Number(people[0].userId) !== Number(meId))) return null;
  return (
    <div className="sr-avatars" title={`On this run: ${peopleText(people, meId) || '—'}`} aria-label={`On this run: ${peopleText(people, meId) || '—'}`}>
      {people.map(p => (
        <span key={p.userId} className={`sr-avatar${Number(p.userId) === Number(meId) ? ' me' : ''}`} aria-hidden="true">{initials(p.name)}</span>
      ))}
    </div>
  );
}

/**
 * @param {object} props
 * @param {object} props.work  GET /api/statements/work/:runId
 * @param {number} props.meId
 * @param {function} [props.onTakeOver]
 * @param {'strip'|'avatars'} [props.variant]
 * @param {boolean} [props.showPeople] - the strip names who is on the run (default true)
 */
function StatementRunPeople({ work, meId, onTakeOver, variant = 'strip', showPeople = true }) {
  const offerTakeOver = !!(work?.ready && !work.missing && work.canTakeOver && onTakeOver && (work.people || []).length);
  useSlideOutButton([offerTakeOver]);
  if (variant === 'avatars') return <PeopleAvatars work={work} meId={meId} />;
  if (!work?.ready || work.missing) return null;
  const people = work.people || [];
  const handedBy = work.me && work.me.addedHow === 'handed_over' && work.me.addedBy !== meId ? work.me : null;
  const shared = people.length > 1 || handedBy || (work.leftPeople || []).length > 0;
  const change = lastChangeText(work, meId);
  const sent = deliveriesText(work.deliveries);
  if (!shared && !change && !offerTakeOver) return null;
  if (!showPeople && !handedBy && !change && !sent.length && !offerTakeOver) return null;
  return (
    <div className="handover-strip" role="status">
      <span className="material-symbols-rounded handover-strip-icon">{handedBy ? 'move_to_inbox' : 'groups'}</span>
      <div className="handover-strip-text">
        {showPeople && (
          <span>
            On this run: <strong>{peopleText(people, meId) || '—'}</strong>
            {work.isDraft ? ' · not generated yet' : ''}
          </span>
        )}
        {handedBy && (
          <span>
            {(work.accounts?.handedTo || []).map(Number).includes(Number(meId))
              ? 'Handed to you for Accounts\' check by '
              : 'Handed over to you by '}
            {handedBy.addedByName || 'a colleague'}{handedBy.addedAt ? `, ${whenText(handedBy.addedAt)}` : ''}
            {handedBy.message ? <>: <q className="handover-message-inline">{handedBy.message}</q></> : '.'}
          </span>
        )}
        {change && <span className="handover-hint">{change}</span>}
        {sent.length > 0 && <span className="handover-hint">Already done on this run — {sent.join('; ')}.</span>}
      </div>
      {offerTakeOver && (
        <div className="buttons slideout">
          <div>
            <button className="btn-slideout always-out" onClick={onTakeOver}
              title="Join this run so it is yours to finish — anyone already on it stays on it">
              <div className="icon-wrapper"><span className="material-symbols-rounded">assignment_ind</span></div>
              <span className="btn-label">Take over this run</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default StatementRunPeople;
